import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";
import { FaEye } from "react-icons/fa";

const TicketsPage = () => {
  const [records, setRecords] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [priorityList, setPriorityList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);

  const [addData, setAddData] = useState({
    subject: "",
    helpdesk_id: "",
    ticket_priority: "",
    asset_data: "",
    incident_asset_id: "",
    location_id: "",
    description: "",
    due_date: "",
    incident_file: "",
  });

  const [editData, setEditData] = useState(null);

  // For React-Select controlled location values
  const [selectedAddLocation, setSelectedAddLocation] = useState(null);
  const [selectedEditLocation, setSelectedEditLocation] = useState(null);

  useEffect(() => {
    loadTickets();
    loadFormData();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return { Authorization: `Bearer ${token}` };
  };

  const loadTickets = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/helpdesk_list", {
        headers: getAuthHeader(),
      });
      setRecords(res.data.data || []);
    } catch (err) {
      console.error("Error loading tickets:", err);
    }
  };

  const loadFormData = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/tickets/form-data", {
        headers: getAuthHeader(),
      });
      setPriorityList(res.data.priority_list || []);
      setDepartments(res.data.helpdesk_departments || []);
      setLocations(res.data.locations || []);
      setAssets(res.data.assets || []);
    } catch (err) {
      console.error("Error loading form-data:", err);
    }
  };

  /* ---------------------- ADD FORM HANDLERS ---------------------- */

  const handleAddChange = (e) =>
    setAddData({ ...addData, [e.target.name]: e.target.value });

  // When asset is selected in ADD form → auto-select matching location
  const handleAddAssetSelect = (value) => {
    const [asset_id, loc_id] = value.split("-");

    // find location by loc_id
    const loc = locations.find((l) => String(l.loc_id) === String(loc_id));

    setAddData((prev) => ({
      ...prev,
      asset_data: value,
      incident_asset_id: asset_id,
      location_id: loc_id,
    }));

    if (loc) {
      setSelectedAddLocation({
        value: loc.loc_id,
        label: `${loc.block_name} / ${loc.floor_name} / ${loc.place_name}`,
      });
    } else {
      setSelectedAddLocation(null);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("add_type", "ticket");
    Object.keys(addData).forEach((key) => {
      fd.append(key, addData[key] ?? "");
    });

    try {
      await axios.post(API_BASE_URL + "/add_tickets", fd, {
        headers: getAuthHeader(),
      });
      toast.success("Ticket Created Successfully!", {
        position: "bottom-right",
      });

      // Reset add form (optional)
      setAddData({
        subject: "",
        helpdesk_id: "",
        ticket_priority: "",
        asset_data: "",
        incident_asset_id: "",
        location_id: "",
        description: "",
        due_date: "",
        incident_file: "",
      });
      setSelectedAddLocation(null);

      setShowAdd(false);
      loadTickets();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Error creating ticket", {
        position: "bottom-right",
      });
    }
  };

  /* ---------------------- EDIT MODAL HANDLERS ---------------------- */

  const openEditModal = async (ticket_id) => {
    try {
      const res = await axios.get(API_BASE_URL + `/tickets/${ticket_id}`, {
        headers: getAuthHeader(),
      });

      const t = res.data.ticket;

      setEditData({
        ticket_id: t.ticket_id,
        subject: t.subject,
        helpdesk_id: t.helpdesk_id,
        ticket_priority: t.ticket_priority,
        incident_asset_id: t.asset_id, // may be null
        location_id: t.location_id,
        description: t.description,
        due_date: t.due_date,
      });

      // Pre-select location in React-Select for edit form
      const loc = locations.find(
        (l) => String(l.loc_id) === String(t.location_id)
      );
      if (loc) {
        setSelectedEditLocation({
          value: loc.loc_id,
          label: `${loc.block_name} / ${loc.floor_name} / ${loc.place_name}`,
        });
      } else {
        setSelectedEditLocation(null);
      }

      setShowEdit(true);
    } catch (err) {
      console.error("Error loading ticket:", err);
      toast.error("Unable to load ticket details", {
        position: "bottom-right",
      });
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    const fd = new FormData();
    Object.keys(editData).forEach((key) => {
      fd.append(key, editData[key] ?? "");
    });

    try {
      await axios.post(
        API_BASE_URL + `/tickets/${editData.ticket_id}/update`,
        fd,
        { headers: getAuthHeader() }
      );

      toast.success("Ticket Updated Successfully!", {
        position: "bottom-right",
      });
      setShowEdit(false);
      setSelectedEditLocation(null);
      loadTickets();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Update failed!", {
        position: "bottom-right",
      });
    }
  };

  /* ---------------------- TABLE COLUMNS ---------------------- */

  const columns = [
    {
      name: "Action",
      width: "120px",
      cell: (row) => (
        <>
          <button
               className="btn p-1"
            onClick={() => {
              const url = `${window.location.origin}/ticket-view/${row.ticket_id}`;
              window.location.href = url; // open in same window
            }}
          >
            <FaEye size={16} />
          </button>

          <button
           className="border-0 bg-transparent me-2"
            onClick={() => openEditModal(row.ticket_id)}
          >
            <i
              className="fa fa-pencil text-warning"
              style={{ fontSize: "18px" }}
            ></i>
          </button>
        </>
      ),
    },
    { name: "Ticket Code", selector: (r) => r.ticket_code, sortable: true },
    { name: "Subject", selector: (r) => r.subject,sortable: true },
    { name: "Priority", selector: (r) => r.priority,sortable: true },
    { name: "Status", selector: (r) => r.status ,sortable: true},
    { name: "Date", selector: (r) => r.created_at ,sortable: true},
    { name: "Due Date", selector: (r) => r.complete_by,sortable: true },
  ];

  return (
    <div className="container mt-4">
      <ToastContainer />

      <div className="card shadow-sm">
        <div className="d-flex justify-content-end p-3">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              if (showAdd) {
                // closing
                setShowAdd(false);
                setSelectedAddLocation(null);
              } else {
                setShowAdd(true);
              }
            }}
          >
            {showAdd ? "Close" : "Add New"}
          </button>
        </div>

        <div className="card-header">
          <h6 className="mb-0">Ticket List</h6>
        </div>

        <div className="card-body">
          {/* ---------------- ADD TICKET FORM ---------------- */}
          {showAdd && (
            <div className="p-3 mb-4 border rounded bg-light">
              <form onSubmit={handleAddSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3 required-tooltip">
                    <label>Subject</label>
                    <input
                      name="subject"
                      className="form-control required-input"
                      value={addData.subject}
                      onChange={handleAddChange}
                    />
                  </div>

                 <div className="col-md-6 mb-3">
  <label>Due Date</label>
  <input
    type="date"
    name="due_date"
    className="form-control"
    value={addData.due_date}
    min={new Date().toISOString().split("T")[0]} // future only
    onChange={handleAddChange}
  />
</div>


                  <div className="col-md-6 mb-3">
                    <label>Helpdesk Department</label>
                    <select
                      className="form-control required-input"
                      name="helpdesk_id"
                      value={addData.helpdesk_id}
                      onChange={handleAddChange}
                    >
                      <option value="">Select</option>
                      {departments.map((d) => (
                        <option key={d.department_id} value={d.department_id}>
                          {d.department_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Priority</label>
                    <select
                      name="ticket_priority"
                      className="form-control required-input"
                      value={addData.ticket_priority}
                      onChange={handleAddChange}
                    >
                      <option value="">Select</option>
                      {priorityList.map((p) => (
                        <option key={p.priority_id} value={p.priority_id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label>Assets</label>
                    <Select
                      options={assets.map((a) => ({
                        value: `${a.assets_id}-${a.location_id}`,
                        label: `[${a.company_asset_code}] ${a.asset_title}`,
                      }))}
                      onChange={(opt) => opt && handleAddAssetSelect(opt.value)}
                      placeholder="Search Asset..."
                      isSearchable
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label>Location</label>
                    <Select
                      options={locations.map((l) => ({
                        value: l.loc_id,
                        label: `${l.block_name} / ${l.floor_name} / ${l.place_name}`,
                      }))}
                      value={selectedAddLocation}
                      onChange={(opt) => {
                        if (!opt) return;
                        setSelectedAddLocation(opt);
                        setAddData((prev) => ({
                          ...prev,
                          location_id: opt.value,
                        }));
                      }}
                      placeholder="Search Location..."
                      isSearchable
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label>Description</label>
                    <textarea
                      name="description"
                      rows="4"
                      className="form-control required-input"
                      value={addData.description}
                      onChange={handleAddChange}
                    ></textarea>
                  </div>

                  <div className="col-md-12">
                    <button className="btn btn-primary">Save</button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* ---------------- EDIT TICKET MODAL ---------------- */}
          {showEdit && editData && (
            <div className="modal-overlay">
              <div className="modal-box">
                <h5>Edit Ticket #{editData.ticket_id}</h5>

                <form onSubmit={handleUpdateSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Subject</label>
                      <input
                        className="form-control"
                        value={editData.subject}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            subject: e.target.value,
                          })
                        }
                      />
                    </div>

                   <div className="col-md-6 mb-3">
  <label>Due Date</label>
  <input
    type="date"
    className="form-control"
    value={editData.due_date || ""}
    min={new Date().toISOString().split("T")[0]} // 👈 future only
    onChange={(e) =>
      setEditData({
        ...editData,
        due_date: e.target.value,
      })
    }
  />
</div>


                    <div className="col-md-6 mb-3">
                      <label>Helpdesk Department</label>
                      <Select
                        options={departments.map((d) => ({
                          value: d.department_id,
                          label: d.department_name,
                        }))}
                        value={
                          departments
                            .filter(
                              (d) => d.department_id == editData.helpdesk_id
                            )
                            .map((d) => ({
                              value: d.department_id,
                              label: d.department_name,
                            }))[0] || null
                        }
                        onChange={(opt) =>
                          opt &&
                          setEditData({ ...editData, helpdesk_id: opt.value })
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Priority</label>
                      <Select
                        options={priorityList.map((p) => ({
                          value: p.priority_id,
                          label: p.name,
                        }))}
                        value={
                          priorityList
                            .filter(
                              (p) => p.priority_id == editData.ticket_priority
                            )
                            .map((p) => ({
                              value: p.priority_id,
                              label: p.name,
                            }))[0] || null
                        }
                        onChange={(opt) =>
                          opt &&
                          setEditData({
                            ...editData,
                            ticket_priority: opt.value,
                          })
                        }
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Assets</label>
                      <Select
                        options={assets.map((a) => ({
                          value: `${a.assets_id}-${a.location_id}`,
                          label: `[${a.company_asset_code}] ${a.asset_title}`,
                        }))}
                        value={
                          editData.incident_asset_id
                            ? assets
                                .filter(
                                  (a) =>
                                    a.assets_id == editData.incident_asset_id
                                )
                                .map((a) => ({
                                  value: `${a.assets_id}-${a.location_id}`,
                                  label: `[${a.company_asset_code}] ${a.asset_title}`,
                                }))[0] || null
                            : null
                        }
                        onChange={(opt) => {
                          if (!opt) return;
                          const [asset_id, loc_id] = opt.value.split("-");

                          const loc = locations.find(
                            (l) => String(l.loc_id) === String(loc_id)
                          );

                          setEditData((prev) => ({
                            ...prev,
                            incident_asset_id: asset_id,
                            location_id: loc_id,
                          }));

                          if (loc) {
                            setSelectedEditLocation({
                              value: loc.loc_id,
                              label: `${loc.block_name} / ${loc.floor_name} / ${loc.place_name}`,
                            });
                          } else {
                            setSelectedEditLocation(null);
                          }
                        }}
                        placeholder="Search Asset..."
                        isSearchable
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Location</label>
                      <Select
                        options={locations.map((l) => ({
                          value: l.loc_id,
                          label: `${l.block_name} / ${l.floor_name} / ${l.place_name}`,
                        }))}
                        value={selectedEditLocation}
                        onChange={(opt) => {
                          if (!opt) return;
                          setSelectedEditLocation(opt);
                          setEditData((prev) => ({
                            ...prev,
                            location_id: opt.value,
                          }));
                        }}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Description</label>
                      <textarea
                        className="form-control required-input"
                        rows="4"
                        value={editData.description}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            description: e.target.value,
                          })
                        }
                      ></textarea>
                    </div>
                    <div className="col-md-12 mt-3 d-flex justify-content-end">
<div className="row">
  <button
    type="button"
    className="btn btn-secondary mx-3"
    onClick={() => {
      setShowEdit(false);
      setSelectedEditLocation(null);
    }}
  >
    Close
  </button>

  <button className="btn btn-primary ms-5">
    Update
  </button>
</div>



                  </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ---------------- DATA TABLE ---------------- */}
          <DataTable
            columns={columns}
            data={records}
            striped
            highlightOnHover
            pagination
            paginationPerPage={10}
          />
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }
        .modal-box {
          background: white;
          padding: 25px;
          width: 60%;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default TicketsPage;
